import {
  applyOperations,
  BOARD_PAGE_SIZES,
  canEditBoard,
  canManageUsers,
  copyBoardSnapshot,
  denormalize,
  DomainError,
  LIMITS,
  newId,
  normalize,
  type BoardAccess,
  type BoardKind,
  type BoardSnapshot,
  type BoardSummary,
  type Card,
  type CommandResult,
  type NormalizedBoard,
  type Operation,
  type ProjectSummary,
} from '@blueprint/domain'
import { exampleBoard } from '@blueprint/domain/fixtures'
import { useSessionStore } from './session-store.js'
import { useUiStore } from './ui-store.js'
import { create } from 'zustand'
import {
  createRepository,
  RepositoryError,
  SaveQueue,
  type BoardRepository,
  type BoardVersion,
  type CardVersion,
  type DataMode,
  type SaveState,
} from '../data/index.js'

export type Phase = 'idle' | 'loading' | 'ready' | 'missing' | 'error'

export interface UndoEntry {
  label: string
  forward: Operation[]
  inverse: Operation[]
}

interface BoardStore {
  mode: DataMode
  durability: string
  phase: Phase
  error: string | null
  boards: BoardSummary[]
  /** The window `boards` is a page of, and how many there are in total. */
  boardsPage: { total: number; limit: number; offset: number }
  /** The dashboard filter: one kind of board, or every kind. */
  boardsKind: BoardKind | null
  /** The projects this user reaches, for the dashboard's sidebar. */
  projects: ProjectSummary[]
  /** What the dashboard lists: one project's items, or the items shared one by one. */
  selectedProject: string | 'shared' | null
  activeBoardId: string | null
  entities: NormalizedBoard | null
  versions: BoardVersion[]
  versionsLoading: boolean
  viewedVersion: { revision: number; snapshot: BoardSnapshot } | null
  past: UndoEntry[]
  future: UndoEntry[]
  save: SaveState

  initialise: () => Promise<void>
  openBoard: (boardId: string) => Promise<void>
  refreshVersions: () => Promise<void>
  syncRemote: () => Promise<void>
  viewVersion: (revision: number | null) => Promise<void>
  createBoard: (name: string) => Promise<string | null>
  deleteBoard: (boardId: string) => Promise<void>
  importSnapshot: (snapshot: BoardSnapshot) => Promise<string | null>

  /** The single entry point for every committed edit. */
  run: (produce: (state: NormalizedBoard) => CommandResult | null) => void
  undo: () => void
  redo: () => void

  /** Ask storage for a different window of the board list. */
  showBoardsPage: (window: { limit?: number; offset?: number; kind?: BoardKind | null }) => Promise<void>
  /** Reloads the project list, keeping the selection when it still exists. */
  loadProjects: () => Promise<void>
  /** Lists a project's items, or the items shared one by one, from page one. */
  selectProject: (selection: string | 'shared') => Promise<void>
  retrySave: () => void
  discardUnsaved: () => Promise<void>
  dismissError: () => void
}

const EMPTY_SAVE: SaveState = { status: 'saved', error: null, pending: 0, revision: 0, serverRevision: null }

let repository: BoardRepository = createRepository()

const queue = new SaveQueue(repository, {
  sync: () => useUiStore.getState().linkSync,
  onState: (boardId, state) => {
    // Acknowledgements stay isolated by board id.
    if (useBoardStore.getState().activeBoardId !== boardId) return
    useBoardStore.setState({ save: state })
  },
  onAck: (boardId, ack) => {
    const store = useBoardStore.getState()
    if (store.activeBoardId !== boardId || !store.entities) return
    // The client's optimistic state stays authoritative; only the revision is adopted,
    // so edits made while the write was in flight are preserved.
    if (store.entities.board.revision === ack.revision) return
    const board = { ...store.entities.board, revision: ack.revision }
    useBoardStore.setState({
      entities: { ...store.entities, board },
      boards: withSummary(store.boards, board),
    })
  },
})

function describe(error: unknown): string {
  if (error instanceof RepositoryError || error instanceof DomainError) return error.message
  if (error instanceof Error) return error.message
  return 'Something went wrong.'
}

/** Set while `initialise` is running, so a second call joins the first rather than racing it. */
let inFlightInitialise: Promise<void> | null = null

/**
 * Whether this user should be given the example board when they have none. A tenant
 * administrator setting up a new tenant should; anyone else is simply someone nothing has
 * been shared with yet, and a demo board would misrepresent that as "here is your work".
 */
function canSeedDemoBoard(): boolean {
  const user = useSessionStore.getState().user
  return user ? canManageUsers(user) : false
}

/** Keep the board picker in step with an optimistic rename of the open board. */
function withSummary(boards: BoardSummary[], board: NormalizedBoard['board']): BoardSummary[] {
  const index = boards.findIndex((summary) => summary.id === board.id)
  if (index === -1) return boards
  const current = boards[index]!
  if (current.name === board.name && current.revision === board.revision && current.updatedAt === board.updatedAt) {
    return boards
  }
  const next = [...boards]
  // Counts come from storage with the list; a local rename must not invent new ones.
  next[index] = { ...current, name: board.name, revision: board.revision, updatedAt: board.updatedAt }
  return next
}

/**
 * Reloads the current window. The offset is kept so an edit does not throw the reader
 * back to page one, but it is pulled back when the page it pointed at no longer exists —
 * deleting the last board on page three should show page two, not an empty table.
 */
async function refreshBoards(window: { limit?: number; offset?: number; kind?: BoardKind | null } = {}): Promise<BoardSummary[]> {
  const current = useBoardStore.getState().boardsPage
  const limit = window.limit ?? current.limit
  let offset = window.offset ?? current.offset
  const kindFilter = window.kind === undefined ? useBoardStore.getState().boardsKind : window.kind
  const kind = kindFilter ?? undefined
  const selected = useBoardStore.getState().selectedProject
  const scope = selected === 'shared' ? { shared: true } : selected ? { projectId: selected } : {}
  let page = await repository.listBoards({ limit, offset, kind, ...scope })
  if (page.boards.length === 0 && page.total > 0 && offset >= page.total) {
    offset = Math.max(0, (Math.ceil(page.total / limit) - 1) * limit)
    page = await repository.listBoards({ limit, offset, kind, ...scope })
  }
  useBoardStore.setState({
    boards: page.boards,
    boardsPage: { total: page.total, limit: page.limit, offset: page.offset },
    boardsKind: kindFilter,
  })
  return page.boards
}

/** For the mindmap store: the dashboard list after a mindmap is created, renamed or saved. */
export function refreshBoardList(): Promise<BoardSummary[]> {
  return refreshBoards()
}

/** The repository in use; the mindmap store shares it. */
export function getRepository(): BoardRepository {
  return repository
}

/** The project new items go into: the selected one, else the server's default. */
export function targetProjectId(): string | undefined {
  const selected = useBoardStore.getState().selectedProject
  return selected && selected !== 'shared' ? selected : undefined
}

/** Keeps the selection when it still exists; otherwise the first project, or the shared list when there is none. */
async function refreshProjects(): Promise<void> {
  const projects = await repository.listProjects()
  const selected = useBoardStore.getState().selectedProject
  const keep = selected === 'shared' || projects.some((project) => project.id === selected)
  useBoardStore.setState({ projects, selectedProject: keep ? selected : (projects[0]?.id ?? 'shared') })
}

/** The board list holds every kind; only a story map opens in the board view. */
const firstStoryMap = (boards: BoardSummary[]): BoardSummary | undefined => boards.find((board) => board.kind === 'storymap')

export const useBoardStore = create<BoardStore>((set, get) => ({
  mode: repository.mode,
  durability: repository.durability,
  phase: 'idle',
  error: null,
  boards: [],
  boardsPage: { total: 0, limit: BOARD_PAGE_SIZES[0], offset: 0 },
  boardsKind: null,
  projects: [],
  selectedProject: null,
  activeBoardId: null,
  entities: null,
  versions: [],
  versionsLoading: false,
  viewedVersion: null,
  past: [],
  future: [],
  save: EMPTY_SAVE,

  async initialise() {
    // One at a time. React invokes mount effects twice in development, and the demo
    // board below is given a fresh id on every copy — so two overlapping runs would each
    // import one and the user would arrive to two demo boards.
    inFlightInitialise ??= (async () => {
      set({ phase: 'loading', error: null })
      try {
        const page = await repository.listBoards()
        // A tenant that has never had a board gets the example one, so its first
        // administrator does not arrive at an empty screen with nothing to look at.
        //
        // Only for an administrator: an ordinary user with no boards has not been given
        // one yet, and handing them a demo board would say they had.
        if (page.total === 0 && canSeedDemoBoard()) {
          // Into the user's default project, which the server creates when they have none.
          await repository.importBoard(copyBoardSnapshot(exampleBoard()))
        }
        // A fresh start (sign-in) lands on the first project, never a previous user's choice.
        set({ selectedProject: null })
        await refreshProjects()
        const boards = await refreshBoards({ offset: 0 })
        const first = firstStoryMap(boards)
        if (first) await get().openBoard(first.id)
        else set({ phase: 'ready', entities: null, activeBoardId: null })
      } catch (error) {
        set({ phase: 'error', error: describe(error) })
      }
    })()

    try {
      await inFlightInitialise
    } finally {
      // Cleared so a later sign-in starts a fresh one.
      inFlightInitialise = null
    }
  },

  async openBoard(boardId) {
    const current = get().activeBoardId
    if (current === boardId && get().phase === 'ready') return

    // Finish saving the board we are leaving before we let go of it.
    if (current && current !== boardId && queue.hasUnsavedWork(current)) {
      try {
        await queue.flush(current)
      } catch {
        set({ error: 'The previous board still has unsaved changes. Retry or discard them before switching.' })
        return
      }
    }

    set({ phase: 'loading', error: null, viewedVersion: null, versions: [], versionsLoading: false })
    try {
      const snapshot = await repository.getBoard(boardId)
      if (current && current !== boardId) queue.forget(current)
      queue.track(boardId, snapshot.board.revision)
      set({
        phase: 'ready',
        activeBoardId: boardId,
        entities: normalize(snapshot),
        viewedVersion: null,
        // History is session-local and resets whenever the active board changes.
        past: [],
        future: [],
        save: queue.state(boardId),
      })
    } catch (error) {
      if (error instanceof RepositoryError && error.kind === 'not_found') {
        set({ phase: 'missing', activeBoardId: boardId, entities: null, error: null })
        await refreshBoards()
        return
      }
      set({ phase: 'error', error: describe(error) })
    }
  },

  async refreshVersions() {
    const boardId = get().activeBoardId
    if (!boardId || !repository.listBoardVersions) return
    set({ versionsLoading: true })
    try {
      const versions = await repository.listBoardVersions(boardId)
      if (get().activeBoardId === boardId) set({ versions, versionsLoading: false })
    } catch (error) {
      if (get().activeBoardId === boardId) set({ error: describe(error), versionsLoading: false })
    }
  },

  async syncRemote() {
    const { activeBoardId: boardId, entities, phase, viewedVersion } = get()
    if (!boardId || !entities || phase !== 'ready' || viewedVersion || !repository.getBoardRevision) return
    if (queue.hasUnsavedWork(boardId) || get().save.status !== 'saved' || useUiStore.getState().editor) return
    try {
      const revision = await repository.getBoardRevision(boardId)
      if (revision === entities.board.revision) return
      if (get().activeBoardId !== boardId || get().viewedVersion || queue.hasUnsavedWork(boardId)) return
      const snapshot = await repository.getBoard(boardId)
      if (get().activeBoardId !== boardId || get().viewedVersion || queue.hasUnsavedWork(boardId)
        || get().save.status !== 'saved' || useUiStore.getState().editor) return
      if (snapshot.board.revision <= (get().entities?.board.revision ?? -1)) return
      queue.track(boardId, snapshot.board.revision)
      set({
        entities: normalize(snapshot), boards: withSummary(get().boards, snapshot.board),
        past: [], future: [], save: queue.state(boardId), error: null,
      })
      void get().refreshVersions()
    } catch {
      // Background checks are best-effort. A transient outage must not interrupt editing.
    }
  },

  async viewVersion(revision) {
    const boardId = get().activeBoardId
    if (!boardId) return
    if (revision === null && !get().viewedVersion) return
    try {
      if (queue.hasUnsavedWork(boardId)) await queue.flush(boardId)
      const snapshot = revision === null
        ? await repository.getBoard(boardId)
        : await repository.getBoardVersion?.(boardId, revision)
      if (!snapshot || get().activeBoardId !== boardId) return
      if (revision === null) {
        queue.track(boardId, snapshot.board.revision)
        set({
          entities: normalize(snapshot), viewedVersion: null, past: [], future: [],
          save: queue.state(boardId), error: null,
        })
      } else {
        set({ viewedVersion: { revision, snapshot }, past: [], future: [], error: null })
      }
    } catch (error) {
      set({ error: describe(error) })
    }
  },

  async createBoard(name) {
    try {
      // Client-generated id: retrying a lost create cannot produce a second board.
      const projectId = targetProjectId()
      const snapshot = await repository.createBoard({ id: newId(), name: name.trim() || 'New board', ...(projectId ? { projectId } : {}) })
      await refreshProjects()
      await refreshBoards()
      await get().openBoard(snapshot.board.id)
      return snapshot.board.id
    } catch (error) {
      set({ error: describe(error) })
      return null
    }
  },

  async deleteBoard(boardId) {
    try {
      if (queue.hasUnsavedWork(boardId)) await queue.flush(boardId)
      const summary = get().boards.find((board) => board.id === boardId)
      const revision = boardId === get().activeBoardId ? queue.state(boardId).revision : (summary?.revision ?? 0)
      await repository.deleteBoard(boardId, revision)
      queue.forget(boardId)
      await refreshProjects()
      const boards = await refreshBoards()
      if (get().activeBoardId === boardId) {
        const next = firstStoryMap(boards)
        if (next) await get().openBoard(next.id)
        else set({ phase: 'ready', activeBoardId: null, entities: null, viewedVersion: null, versions: [], past: [], future: [], save: EMPTY_SAVE })
      }
    } catch (error) {
      set({ error: describe(error) })
    }
  },

  async importSnapshot(snapshot) {
    try {
      const created = await repository.importBoard(snapshot, targetProjectId())
      await refreshProjects()
      await refreshBoards()
      await get().openBoard(created.board.id)
      return created.board.id
    } catch (error) {
      set({ error: describe(error) })
      return null
    }
  },

  async showBoardsPage(window) {
    try {
      await refreshBoards(window)
    } catch (error) {
      set({ error: describe(error) })
    }
  },

  async loadProjects() {
    try {
      await refreshProjects()
      await refreshBoards()
    } catch (error) {
      set({ error: describe(error) })
    }
  },

  async selectProject(selection) {
    set({ selectedProject: selection })
    try {
      await refreshBoards({ offset: 0 })
    } catch (error) {
      set({ error: describe(error) })
    }
  },

  run(produce) {
    if (get().viewedVersion) return
    const { entities, activeBoardId, past } = get()
    if (!entities || !activeBoardId) return
    // Checked again on the server, which is what actually enforces it. Stopping here
    // keeps a viewer's screen honest: no optimistic edit that is about to be rejected.
    if (!canEditBoard(activeBoardAccess())) {
      set({ error: 'This board is shared with you for viewing only.' })
      return
    }
    let result: CommandResult | null
    try {
      result = produce(entities)
    } catch (error) {
      set({ error: describe(error) })
      return
    }
    if (!result) return

    let next: NormalizedBoard
    try {
      next = applyOperations(entities, result.forward).state
    } catch (error) {
      set({ error: describe(error) })
      return
    }

    set({
      entities: next,
      boards: withSummary(get().boards, next.board),
      // A new action clears redo; history is capped at LIMITS.undoDepth entries.
      past: [...past, { label: result.label, forward: result.forward, inverse: result.inverse }].slice(-LIMITS.undoDepth),
      future: [],
      error: null,
    })
    queue.enqueue(activeBoardId, result.forward)
  },

  undo() {
    if (get().viewedVersion) return
    const { entities, activeBoardId, past, future } = get()
    const entry = past.at(-1)
    if (!entities || !activeBoardId || !entry) return
    try {
      const next = applyOperations(entities, entry.inverse).state
      set({
        entities: next,
        boards: withSummary(get().boards, next.board),
        past: past.slice(0, -1),
        future: [...future, entry],
        error: null,
      })
      // Undo is a new queued mutation, not a cancellation of the original write.
      queue.enqueue(activeBoardId, entry.inverse)
    } catch (error) {
      set({ error: `Undo is no longer possible: ${describe(error)}`, past: [], future: [] })
    }
  },

  redo() {
    if (get().viewedVersion) return
    const { entities, activeBoardId, past, future } = get()
    const entry = future.at(-1)
    if (!entities || !activeBoardId || !entry) return
    try {
      const next = applyOperations(entities, entry.forward).state
      set({
        entities: next,
        boards: withSummary(get().boards, next.board),
        future: future.slice(0, -1),
        past: [...past, entry],
        error: null,
      })
      queue.enqueue(activeBoardId, entry.forward)
    } catch (error) {
      set({ error: `Redo is no longer possible: ${describe(error)}`, past: [], future: [] })
    }
  },

  retrySave() {
    const boardId = get().activeBoardId
    if (boardId) queue.retry(boardId)
  },

  async discardUnsaved() {
    const boardId = get().activeBoardId
    if (!boardId) return
    queue.discard(boardId)
    // The optimistic board no longer matches storage, so reload it.
    set({ activeBoardId: null })
    await get().openBoard(boardId)
  },

  dismissError() {
    set({ error: null })
  },
}))

/** Unacknowledged operations, offered as a JSON download when a save cannot proceed. */
export function unsavedOperations(boardId: string): Operation[] {
  return queue.unsavedOperations(boardId)
}

export function hasUnsavedWork(): boolean {
  return queue.hasUnsavedWork()
}

export function currentSnapshot(): BoardSnapshot | null {
  const entities = useBoardStore.getState().entities
  return entities ? denormalize(entities) : null
}

/** Test seam: swap the adapter and reset every queue. */
export function __setRepository(next: BoardRepository): void {
  repository = next
  queue.setRepository(next)
  useBoardStore.setState({
    mode: next.mode,
    durability: next.durability,
    phase: 'idle',
    boards: [],
    projects: [],
    selectedProject: null,
    activeBoardId: null,
    entities: null,
    viewedVersion: null,
    versions: [],
    versionsLoading: false,
    past: [],
    future: [],
    save: EMPTY_SAVE,
    error: null,
  })
}

/**
 * How the signed-in user reaches the board that is open. Taken from the board list, which
 * is the API's own answer — the browser never decides this for itself.
 *
 * Absent while the list is loading, which is treated as read-only: better to disable a
 * control for a moment than to offer an edit the server will refuse.
 */
export function activeBoardAccess(): BoardAccess | null {
  const state = useBoardStore.getState()
  if (!state.activeBoardId) return null
  return state.boards.find((entry) => entry.id === state.activeBoardId)?.access ?? null
}

/**
 * A card's saved versions on the open board, newest first, or null when this repository
 * keeps no card history (the in-memory test repository).
 */
export async function loadCardVersions(cardId: string): Promise<CardVersion[] | null> {
  const boardId = useBoardStore.getState().activeBoardId
  if (!boardId || !repository.listCardVersions) return null
  return repository.listCardVersions(boardId, cardId)
}

export async function loadCardVersion(cardId: string, version: number): Promise<Card> {
  const boardId = useBoardStore.getState().activeBoardId
  if (!boardId || !repository.getCardVersion) throw new Error('Card history is not available.')
  return repository.getCardVersion(boardId, cardId, version)
}

/** True when the open board may be edited. Drives every editing control in the UI. */
export function useBoardEditable(): boolean {
  return useBoardStore((state) => {
    if (!state.activeBoardId) return false
    const summary = state.boards.find((entry) => entry.id === state.activeBoardId)
    return summary ? canEditBoard(summary.access) : false
  })
}
